// Seiten-Baukasten: Seiten der Hörerseite bauen, ansehen und veröffentlichen.
//
// Links die Blöcke, in der Mitte die Vorschau, rechts die Einstellungen.
// Gespeichert wird immer erst als Entwurf; veröffentlichen darf nur die Leitung.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  BLOCKS,
  DEFAULT_SITE_SETTINGS,
  DENSITY,
  FONT_SETS,
  RADIUS,
  blockId,
  type BlockKind,
  type FieldDef,
  type PageBlock,
  type SiteSettings,
} from "@webradiosuite/shared/pages";
import { Link, useSearchParams } from "react-router-dom";
import { can } from "@webradiosuite/shared";
import { bereichSiteApi, kanalSeiteApi, siteApi, type ContactMessage, type SeitenBereich, type SiteOverview, type SitePageDetail, type SitePageSummary } from "../api";
import { useAuth } from "../auth/AuthProvider";
import { BlockForm } from "../components/block-fields";
import { formatBytes, useMediaLibrary, useMedienKanal } from "../components/media-picker";
import { Initialen, Meldungen, Sym } from "../components/social-ui";
import { Badge, Callout, Card, EmptyState, Page, PageHeader, Skeleton, Tabs } from "../components/ui";

type Tab = "pages" | "look" | "images" | "contact";

/**
 * Der Bereich, in dem gerade gebaut wird (f-web): die Radio-Hauptseite oder
 * die eigene Hörerseite eines Kanals. Alles darunter – Liste, Editor,
 * Vorlagen, Vorschau, Reihenfolge – arbeitet nur in diesem Bereich.
 */
interface Bereich {
  /** null = Radio-Hauptseite. */
  channelId: string | null;
  kanal: SeitenBereich | null;
  /** Pfad der Startseite hinter der Basisadresse, z. B. „/radio“ oder „/radio/sender/rock“. */
  pfad: string;
  /** Veröffentlichen, Löschen, Vorlagen: radioweit nur Admins, im Kanal alle, die dort pflegen dürfen. */
  darfLeiten: boolean;
}

export function SiteBuilderPage(): JSX.Element {
  const { user } = useAuth();
  const isAdmin = user?.role === "tenant_admin" || user?.role === "super_admin";
  const radioErlaubt = user?.role ? can(user.role, "site.write") : false;
  const [params, setParams] = useSearchParams();
  const kanalSlug = params.get("kanal");
  const [bereiche, setBereiche] = useState<SeitenBereich[] | null>(null);
  const [overview, setOverview] = useState<SiteOverview | null>(null);
  const [tab, setTab] = useState<Tab>("pages");
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Welche Kanäle zur Wahl stehen: eingeschaltete Seite und Recht zum Pflegen.
  useEffect(() => {
    kanalSeiteApi.bereiche()
      .then((b) => setBereiche(b.filter((x) => x.aktiv && x.rechte.pflegen)))
      .catch(() => setBereiche([]));
  }, []);

  const gewaehlt = bereiche?.find((b) => b.slug === kanalSlug) ?? null;
  // Wer nur über eine Kanalrolle hier ist, landet ohne Angabe im ersten Kanal.
  const kanal = gewaehlt ?? (!radioErlaubt && bereiche && bereiche.length > 0 ? bereiche[0]! : null);
  const unbekannt = kanalSlug !== null && bereiche !== null && !gewaehlt;
  const bereich: Bereich | null = bereiche === null || !overview ? null : {
    channelId: kanal?.channel_id ?? null,
    kanal,
    pfad: kanal ? `/${overview.tenant_slug}/sender/${kanal.slug}` : `/${overview.tenant_slug}`,
    darfLeiten: kanal ? kanal.rechte.pflegen : isAdmin,
  };
  const channelId = kanal?.channel_id ?? null;
  useMedienKanal(channelId);

  const waehle = (slug: string): void => {
    setEditing(null);
    setTab("pages");
    setOverview(null);
    const next = new URLSearchParams(params);
    if (slug) next.set("kanal", slug); else next.delete("kanal");
    setParams(next);
  };

  // Wer schnell zwischen Bereichen wechselt, soll nicht die Liste des
  // vorigen Bereichs zu sehen bekommen, weil deren Antwort später eintraf.
  const aktuell = useRef<string | null>(null);
  const reload = useCallback(async () => {
    aktuell.current = channelId;
    try {
      const res = await bereichSiteApi.overview(channelId);
      if (aktuell.current !== channelId) return;
      setOverview(res);
      setError(null);
    } catch {
      setError("Die Seiten ließen sich nicht laden.");
    }
  }, [channelId]);

  useEffect(() => {
    if (bereiche === null) return;
    if (!radioErlaubt && !kanal) return;
    void reload();
  }, [reload, bereiche, radioErlaubt, kanal]);

  const wahl = bereiche && bereiche.length > 0 && (
    <BereichsWahl bereiche={bereiche} radio={radioErlaubt} wert={kanal?.slug ?? ""} onChange={waehle} />
  );

  // Kein Recht radioweit und kein Kanal mit eigener Seite: nichts zu bauen.
  if (bereiche !== null && !radioErlaubt && !kanal) {
    return (
      <Page className="max-w-6xl">
        <PageHeader eyebrow="Aussehen" title="Hörerseite gestalten" />
        <div className="card p-2">
          <EmptyState icon={<Sym name="layout" className="h-5 w-5" />} title="Hier gibt es für dich gerade nichts zu bauen."
            description="Seiten bauen können Kanäle mit eingeschalteter eigener Hörerseite, in denen du Seiten pflegen darfst." />
        </div>
      </Page>
    );
  }

  if (!overview || !bereich) {
    return (
      <Page className="max-w-6xl">
        <PageHeader eyebrow="Aussehen" title="Hörerseite gestalten">{wahl || null}</PageHeader>
        {error ? <Callout tone="danger">{error}</Callout> : (
          <div className="space-y-3" aria-label="Wird geladen …">
            <Skeleton className="h-28" />
            <Skeleton className="h-64" />
          </div>
        )}
      </Page>
    );
  }

  if (editing) {
    return (
      <PageEditor
        pageId={editing}
        overview={overview}
        isAdmin={bereich.darfLeiten}
        pfad={bereich.pfad}
        onClose={() => { setEditing(null); void reload(); }}
      />
    );
  }

  return (
    <Page className="max-w-6xl">
      <PageHeader
        eyebrow="Aussehen"
        title="Hörerseite gestalten"
        description={kanal ? <>
          Die eigene Hörerseite von „{kanal.name}“: dieselben Bausteine und Vorlagen wie beim Radio, Player und „Jetzt läuft“ zeigen diesen Kanal.
          Nichts geht sofort live – erst wenn ihr auf „Veröffentlichen“ drückt.
        </> : <>
          Hier baut ihr eure Seite aus fertigen Bausteinen zusammen: Player, Musikwunsch, Sendeplan, Texte, Bilder.
          Nichts geht sofort live – erst wenn ihr auf „Veröffentlichen“ drückt.
        </>}
        actions={
          <a href={`${overview.public_url}${bereich.pfad}`} target="_blank" rel="noreferrer" className="btn btn-secondary">
            <Sym name="external" />{kanal ? "Kanalseite öffnen" : "Hörerseite öffnen"}
          </a>
        }
      >
        {wahl}
        {unbekannt && (
          <Callout tone="warning" className="mb-3">
            Für „{kanalSlug}“ ist keine eigene Hörerseite eingeschaltet – oder dir fehlt dort das Recht, Seiten zu pflegen.
            {kanal ? ` Gezeigt wird „${kanal.name}“.` : " Gezeigt wird die Radio-Hauptseite."}
          </Callout>
        )}
        {/* Aussehen, Rechtliches, Bilder und Kontakt gelten radioweit; im Kanal gibt es nur die Seiten. */}
        {!kanal && (
          <Tabs<Tab>
            value={tab}
            onChange={setTab}
            items={[
              { value: "pages", label: "Seiten", count: overview.pages.length },
              { value: "look", label: "Aussehen & Rechtliches" },
              { value: "images", label: "Bilder" },
              { value: "contact", label: "Kontakt", count: overview.contact_open },
            ]}
          />
        )}
      </PageHeader>

      {error && <Callout tone="danger" className="mb-4">{error}</Callout>}

      <div className="animate-fade-in" key={`${kanal?.slug ?? ""}:${tab}`}>
        {(tab === "pages" || kanal) && <PagesTab overview={overview} bereich={bereich} onEdit={setEditing} onChanged={reload} />}
        {!kanal && tab === "look" && <LookTab overview={overview} isAdmin={isAdmin} onChanged={reload} />}
        {!kanal && tab === "images" && <ImagesTab publicUrl={overview.public_url} />}
        {!kanal && tab === "contact" && <ContactTab onChanged={reload} />}
      </div>
    </Page>
  );
}

// ---------------------------------------------------------------- Bereichswahl (f-web)

/** Den deutschen Satz aus einer Fehlerantwort der API holen, falls sie einen mitschickt. */
function serverSatz(err: unknown): string | null {
  const raw = err instanceof Error ? err.message : "";
  try {
    const body = JSON.parse(raw.replace(/^\d{3}\s*/, "")) as { message?: string };
    return body.message ?? null;
  } catch {
    return null;
  }
}

/** „Radio-Hauptseite | Kanal …“ – oben über den Reitern. */
function BereichsWahl({ bereiche, radio, wert, onChange }: {
  bereiche: SeitenBereich[];
  radio: boolean;
  wert: string;
  onChange: (slug: string) => void;
}): JSX.Element | null {
  if (!radio && bereiche.length < 2) return null;
  return (
    <div className="mb-4 flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
      <label htmlFor="seiten-bereich" className="section-label">Bereich</label>
      <select id="seiten-bereich" value={wert} onChange={(e) => onChange(e.target.value)} className="input h-9 !w-auto min-w-0 max-w-full font-medium">
        {radio && <option value="">Radio-Hauptseite</option>}
        {bereiche.map((b) => <option key={b.slug} value={b.slug}>Kanal {b.name}</option>)}
      </select>
      {wert && (
        <Link to={`/channels/${encodeURIComponent(wert)}/hoererseite`} className="text-xs font-medium text-muted underline-offset-2 hover:text-brand-primary hover:underline">
          Name, Logo und Farben des Kanals
        </Link>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Seitenliste

function statusLabel(page: SitePageSummary): { text: string; tone: "warning" | "success" } {
  if (!page.published_at) return { text: "Entwurf", tone: "warning" };
  if (page.has_changes) return { text: "Änderungen nicht veröffentlicht", tone: "warning" };
  return { text: "Veröffentlicht", tone: "success" };
}

/**
 * Das Aussehen einer Vorlage in einem Halbsatz: „kompakt, weiche Ecken".
 *
 * Steht neben dem Häkchen, damit niemand raten muss, was er da übernimmt.
 */
export function beschreibung(look: { font_set?: string; radius?: string; density?: string }): string {
  const teile: string[] = [];
  const dichte: Record<string, string> = { luftig: "luftig", normal: "normale Dichte", kompakt: "kompakt" };
  const ecken: Record<string, string> = { weich: "weiche Ecken", mittel: "leichte Ecken", eckig: "eckig" };
  const schrift: Record<string, string> = { system: "Systemschrift", klassisch: "Serifen-Überschriften", kraeftig: "kräftige Überschriften" };
  if (look.density && dichte[look.density]) teile.push(dichte[look.density]!);
  if (look.radius && ecken[look.radius]) teile.push(ecken[look.radius]!);
  if (look.font_set && schrift[look.font_set]) teile.push(schrift[look.font_set]!);
  return teile.length > 0 ? teile.join(", ") : "Schrift und Abstände";
}

function PagesTab({ overview, bereich, onEdit, onChanged }: {
  overview: SiteOverview;
  bereich: Bereich;
  onEdit: (id: string) => void;
  onChanged: () => Promise<void>;
}): JSX.Element {
  const isAdmin = bereich.darfLeiten;
  const [title, setTitle] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  const create = async (): Promise<void> => {
    if (title.trim() === "") return;
    setBusy(true);
    try {
      const page = await bereichSiteApi.createPage({ title: title.trim() }, bereich.channelId);
      setTitle("");
      await onChanged();
      onEdit(page.id);
    } catch (err) {
      setError(serverSatz(err) ?? "Die Seite ließ sich nicht anlegen.");
    } finally {
      setBusy(false);
    }
  };

  // Je Vorlage merken, ob ihr Aussehen mitkommen soll (nur für diesen Besuch).
  const [looks, setLooks] = useState<Set<string>>(new Set());

  const move = async (index: number, delta: number): Promise<void> => {
    const ids = overview.pages.map((p) => p.id);
    const to = index + delta;
    if (to < 0 || to >= ids.length) return;
    [ids[index], ids[to]] = [ids[to]!, ids[index]!];
    await bereichSiteApi.reorderPages(ids, bereich.channelId);
    await onChanged();
  };

  const useTemplate = async (key: string, label: string, look: boolean): Promise<void> => {
    const replace = overview.pages.length > 0
      && window.confirm(`Vorlage „${label}“ anwenden.\n\nOK: bestehende Seiten ersetzen.\nAbbrechen: Vorlage ergänzen und vorhandene Seiten behalten.`);
    setBusy(true);
    try {
      const res = await bereichSiteApi.applyTemplate(key, replace, look, bereich.channelId);
      setNote(res.message);
      await onChanged();
    } catch (err) {
      setError(serverSatz(err) ?? "Die Vorlage ließ sich nicht anwenden.");
    } finally {
      setBusy(false);
    }
  };

  // Vorlagen: ohne eigene Seiten ganz oben, sonst unter der Liste.
  const vorlagen = isAdmin && (

        <section>
          <div className="section-label mb-1">Vorlagen</div>
          <p className="mb-3 text-sm text-muted">Fertige Seitensätze als Startpunkt – danach könnt ihr alles ändern.</p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {overview.templates.map((t) => (
              <div key={t.key} className="card flex flex-col p-4 transition-shadow hover:shadow-md">
                <div className="flex items-center gap-2">
                  <Sym name="sparkles" className="h-4 w-4 text-brand-primary" />
                  <span className="font-medium text-primary">{t.label}</span>
                </div>
                <p className="mt-1.5 flex-1 text-xs leading-relaxed text-muted">{t.hint}</p>
                {t.look && (
                  <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs text-secondary">
                    <input type="checkbox" checked={looks.has(t.key)} className="mt-0.5"
                      onChange={(e) => setLooks((s) => { const n = new Set(s); if (e.target.checked) n.add(t.key); else n.delete(t.key); return n; })} />
                    <span>Aussehen mitnehmen ({beschreibung(t.look)}). Eure Farben bleiben, wie sie sind.</span>
                  </label>
                )}
                <button onClick={() => void useTemplate(t.key, t.label, looks.has(t.key))} disabled={busy}
                  className="btn btn-secondary btn-sm mt-3 w-full">
                  {t.pages} Seiten anlegen
                </button>
              </div>
            ))}
          </div>
        </section>
  );

  return (
    <div className="space-y-6">
      {overview.pages.length === 0 && (
        <section className="card brand-glow flex flex-wrap items-center gap-4 p-5">
          <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-brand-primary/10 text-brand-primary"><Sym name="layout" className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1 basis-64">
            <h2 className="font-semibold text-primary">Noch keine eigenen Seiten</h2>
            <p className="mt-1 text-sm text-muted">
              {bereich.kanal
                ? <>Die Seite von „{bereich.kanal.name}“ ist noch leer. Wählt eine Vorlage – daraus wird die Startseite des Kanals,
                  die ihr ändern könnt. Die Radio-Hauptseite bleibt davon unberührt.</>
                : <>Eure Hörerseite sieht gerade so aus wie bisher. Wählt eine Vorlage – daraus wird eine Startseite,
                  die ihr ändern könnt. Bis ihr veröffentlicht, bleibt für die Hörer alles beim Alten.</>}
            </p>
          </div>
        </section>
      )}
      {overview.pages.length === 0 && vorlagen}

      <Meldungen error={error} notice={note} onClear={() => setNote(null)} />

      <Card
        padded={false}
        title="Seiten"
        description="Die Reihenfolge hier ist die Reihenfolge im Menü der Hörerseite."
        actions={overview.pages.length > 0 ? <Badge>{overview.pages.length}</Badge> : undefined}
      >
        <ul className="divide-y divide-default">
          {overview.pages.map((page, i) => {
            const status = statusLabel(page);
            return (
              <li key={page.id} className="flex flex-wrap items-center gap-3 px-5 py-3.5">
                <span className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg ${page.is_home ? "bg-brand-primary/10 text-brand-primary" : "bg-input text-muted"}`}
                  style={page.is_home ? undefined : { boxShadow: "inset 0 0 0 1px var(--border)" }}>
                  <Sym name={page.is_home ? "radio" : "layout"} className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1 basis-48">
                  <div className="flex flex-wrap items-center gap-2">
                    <button onClick={() => onEdit(page.id)} className="font-medium text-primary hover:underline" style={{ minHeight: 0 }}>{page.title}</button>
                    {page.is_home && <Badge tone="brand">Startseite</Badge>}
                    <Badge tone={status.tone} dot>{status.text}</Badge>
                    {!page.in_menu && <span className="text-xs text-muted">nicht im Menü</span>}
                  </div>
                  <div className="mt-0.5 font-mono text-xs text-muted">
                    {bereich.pfad}{page.is_home ? "" : `/${page.slug}`} <span className="font-sans">· {page.blocks_count} Bausteine</span>
                  </div>
                </div>
                <span className="flex items-center gap-1">
                  <span className="inline-flex">
                    <button onClick={() => void move(i, -1)} disabled={i === 0} className="btn btn-secondary btn-sm btn-icon rounded-r-none" aria-label={`${page.title} nach oben`}><Sym name="up" className="h-3.5 w-3.5" /></button>
                    <button onClick={() => void move(i, 1)} disabled={i === overview.pages.length - 1} className="btn btn-secondary btn-sm btn-icon -ml-px rounded-l-none" aria-label={`${page.title} nach unten`}><Sym name="down" className="h-3.5 w-3.5" /></button>
                  </span>
                  <a href={`${overview.public_url}${bereich.pfad}${page.is_home ? "" : `/${page.slug}`}`} target="_blank" rel="noreferrer"
                    className="btn btn-ghost btn-sm"><Sym name="external" className="h-3.5 w-3.5" />Ansehen</a>
                  <button onClick={() => onEdit(page.id)} className="btn btn-primary btn-sm">Bearbeiten</button>
                  {isAdmin && !page.is_home && (
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Seite „${page.title}“ wirklich löschen?`)) return;
                        try {
                          await siteApi.deletePage(page.id);
                        } catch (err) {
                          setError(serverSatz(err) ?? "Die Seite ließ sich nicht löschen.");
                        }
                        await onChanged();
                      }}
                      className="btn btn-ghost btn-sm btn-icon !text-red-600 dark:!text-red-400" aria-label="Löschen" title="Löschen"><Sym name="trash" className="h-3.5 w-3.5" /></button>
                  )}
                  {isAdmin && page.is_home && <span className="w-[1.875rem]" aria-hidden="true" />}
                </span>
              </li>
            );
          })}
        </ul>
        <div className="flex flex-wrap items-center gap-2 border-t border-default bg-surface-sunken/50 px-5 py-3">
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titel der neuen Seite, z. B. Kontakt"
            onKeyDown={(e) => { if (e.key === "Enter") void create(); }}
            aria-label="Titel der neuen Seite" className="input h-9 min-w-0 max-w-sm flex-1" />
          <button onClick={() => void create()} disabled={busy || title.trim() === ""}
            className="btn btn-primary"><Sym name="plus" />Seite anlegen</button>
        </div>
      </Card>

      {overview.pages.length > 0 && vorlagen}
    </div>
  );
}

// ---------------------------------------------------------------- Editor

const GROUP_ORDER = ["Radio", "Programm", "Inhalt", "Service"];

function PageEditor({ pageId, overview, isAdmin, pfad, onClose }: {
  pageId: string;
  overview: SiteOverview;
  isAdmin: boolean;
  /** Pfad der Startseite im gewählten Bereich (Radio oder Kanal). */
  pfad: string;
  onClose: () => void;
}): JSX.Element {
  const [page, setPage] = useState<SitePageDetail | null>(null);
  const [blocks, setBlocks] = useState<PageBlock[]>([]);
  const [meta, setMeta] = useState({ title: "", slug: "", menu_label: "", in_menu: true, seo_title: "", seo_description: "" });
  const [selected, setSelected] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [mobile, setMobile] = useState(false);
  const [adding, setAdding] = useState(false);
  const dragFrom = useRef<number | null>(null);

  const load = useCallback(async () => {
    const detail = await siteApi.page(pageId);
    setPage(detail);
    setBlocks(detail.blocks);
    setMeta({
      title: detail.title,
      slug: detail.slug,
      menu_label: detail.menu_label ?? "",
      in_menu: detail.in_menu,
      seo_title: detail.seo_title ?? "",
      seo_description: detail.seo_description ?? "",
    });
    setDirty(false);
  }, [pageId]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    void siteApi.previewLink(pageId).then((res) => setPreviewUrl(res.url)).catch(() => setPreviewUrl(null));
  }, [pageId]);

  const save = async (): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      await siteApi.savePage(pageId, {
        title: meta.title,
        slug: meta.slug,
        menu_label: meta.menu_label.trim() === "" ? null : meta.menu_label.trim(),
        in_menu: meta.in_menu,
        seo_title: meta.seo_title.trim() === "" ? null : meta.seo_title.trim(),
        seo_description: meta.seo_description.trim() === "" ? null : meta.seo_description.trim(),
        blocks,
      });
      setDirty(false);
      setNonce((n) => n + 1);
      setMessage("Entwurf gespeichert.");
      await load();
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message.replace(/^\d+\s*/, "") : "Das Speichern hat nicht geklappt.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const publish = async (): Promise<void> => {
    if (dirty && !(await save())) return;
    setBusy(true);
    try {
      await siteApi.publishPage(pageId);
      setMessage("Die Seite ist jetzt für alle sichtbar.");
      await load();
      setNonce((n) => n + 1);
    } catch (err) {
      setError(serverSatz(err) ?? "Das Veröffentlichen hat nicht geklappt.");
    } finally {
      setBusy(false);
    }
  };

  const change = (next: PageBlock[]): void => { setBlocks(next); setDirty(true); };

  /**
   * Was weiß diese Oberfläche über einen Baustein?
   *
   * Zuerst das eigene Verzeichnis, sonst das, was der Server mitschickt: Wer
   * die Suite offen hatte, während ein Update lief, hat eine ältere
   * Oberfläche vor sich, der Server bietet aber schon den neuen Baustein an.
   * Ohne diesen Rückfall täte ein Klick darauf schlicht nichts – und genau so
   * meldet es sich beim Benutzer: „Ich kann den Baustein nicht anlegen."
   */
  const def = (kind: BlockKind): { label: string; hint: string; fields: FieldDef[]; defaults: Record<string, unknown> } => {
    const eigen = BLOCKS[kind] as (typeof BLOCKS)[BlockKind] | undefined;
    if (eigen) return { label: eigen.label, hint: eigen.hint, fields: eigen.fields, defaults: eigen.defaults };
    const vomServer = overview.blocks.find((b) => b.kind === kind);
    if (vomServer) return { label: vomServer.label, hint: vomServer.hint, fields: vomServer.fields, defaults: vomServer.defaults };
    return { label: kind, hint: "Diesen Baustein kennt diese Oberfläche nicht – bitte die Seite neu laden.", fields: [], defaults: {} };
  };

  const addBlock = (kind: BlockKind): void => {
    const block: PageBlock = { id: blockId(), kind, props: { ...def(kind).defaults } };
    change([...blocks, block]);
    setSelected(block.id);
    setAdding(false);
  };

  const moveBlock = (from: number, to: number): void => {
    if (to < 0 || to >= blocks.length || from === to) return;
    const next = [...blocks];
    const [item] = next.splice(from, 1);
    next.splice(to, 0, item!);
    change(next);
  };

  const current = blocks.find((b) => b.id === selected) ?? null;
  const previewSrc = previewUrl ? `${previewUrl}${previewUrl.includes("?") ? "&" : "?"}t=${nonce}` : null;
  const usedOnce = useMemo(() => new Set(blocks.map((b) => b.kind)), [blocks]);

  if (!page) {
    return (
      <Page>
        <div className="grid gap-4 lg:grid-cols-[260px_1fr_320px]" aria-label="Wird geladen …">
          <Skeleton className="h-64" /><Skeleton className="h-[600px]" /><Skeleton className="h-64" />
        </div>
      </Page>
    );
  }

  // Felder der Seiteneinstellungen: gleiche Form, weniger Wiederholung.
  const metaFeld = (label: string, key: "title" | "slug" | "menu_label" | "seo_title"): JSX.Element => (
    <label className="block text-[0.8rem] font-medium text-secondary">{label}
      <input value={meta[key]} onChange={(e) => { setMeta({ ...meta, [key]: e.target.value }); setDirty(true); }}
        className="input mt-1.5 h-9" />
    </label>
  );

  return (
    <div className="flex h-full flex-col">
      {/* Werkzeugleiste: zurück, Titel und Stand, Ansicht, Speichern, Veröffentlichen */}
      <div className="sticky top-0 z-20 flex flex-wrap items-center gap-2 border-b border-default bg-card/95 px-4 py-3 backdrop-blur sm:px-6">
        <button onClick={() => {
          if (dirty && !window.confirm("Es gibt ungespeicherte Änderungen. Trotzdem zurück?")) return;
          onClose();
        }} className="btn btn-ghost btn-sm"><Sym name="back" className="h-3.5 w-3.5" />Seiten</button>
        <span className="hidden h-6 w-px bg-[var(--border)] sm:block" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate font-semibold text-primary">{meta.title}</span>
            {dirty
              ? <Badge tone="warning" dot>nicht gespeichert</Badge>
              : page.has_changes ? <Badge tone="warning">Änderungen noch nicht veröffentlicht</Badge> : null}
          </div>
          <div className="truncate font-mono text-xs text-muted">{pfad}{page.is_home ? "" : `/${meta.slug}`}</div>
        </div>
        <div className="tabbar" role="group" aria-label="Ansicht">
          <button onClick={() => setMobile(false)} className={`tab flex items-center gap-1.5 ${!mobile ? "tab-active" : ""}`} aria-pressed={!mobile} title="Große Ansicht">
            <Sym name="monitor" className="h-3.5 w-3.5" /><span className="hidden md:inline">Große Ansicht</span>
          </button>
          <button onClick={() => setMobile(true)} className={`tab flex items-center gap-1.5 ${mobile ? "tab-active" : ""}`} aria-pressed={mobile} title="Handy-Ansicht">
            <Sym name="smartphone" className="h-3.5 w-3.5" /><span className="hidden md:inline">Handy-Ansicht</span>
          </button>
        </div>
        <button onClick={() => void save()} disabled={busy || !dirty}
          className="btn btn-secondary">Entwurf speichern</button>
        {isAdmin ? (
          <button onClick={() => void publish()} disabled={busy}
            className="btn btn-primary"><Sym name="send" className="h-3.5 w-3.5" />Veröffentlichen</button>
        ) : (
          <span className="text-xs text-muted">Veröffentlichen darf die Leitung.</span>
        )}
      </div>

      {(message || error) && (
        <div className="px-4 pt-3 sm:px-6" role="status">
          <Callout tone={error ? "danger" : "success"}>{error ?? message}</Callout>
        </div>
      )}

      <div className="grid flex-1 gap-4 p-4 sm:px-6 lg:grid-cols-[260px_minmax(0,1fr)_320px]">
        {/* Bausteine */}
        <div className="min-w-0 space-y-2">
          <div className="flex items-center justify-between">
            <h2 className="section-label">Bausteine</h2>
            <span className="text-xs text-muted">{blocks.length}</span>
          </div>
          <ul className="space-y-1.5">
            {blocks.map((block, i) => (
              <li key={block.id}
                draggable
                onDragStart={() => { dragFrom.current = i; }}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (dragFrom.current !== null) moveBlock(dragFrom.current, i); dragFrom.current = null; }}
                className={`group rounded-lg border bg-card transition-shadow ${selected === block.id ? "border-brand-primary shadow-sm ring-2 ring-brand-primary/20" : "border-default hover:shadow-sm"}`}>
                <div className="flex items-center gap-0.5 py-1 pl-1.5 pr-1">
                  <span className="cursor-grab text-muted" aria-hidden="true"><Sym name="grip" className="h-4 w-4" /></span>
                  <button onClick={() => setSelected(block.id)} className="min-w-0 flex-1 truncate px-1.5 py-1.5 text-left text-sm font-medium text-primary" style={{ minHeight: 0 }}>
                    {def(block.kind).label}
                  </button>
                  <span className="flex items-center opacity-60 transition-opacity group-hover:opacity-100">
                    <button onClick={() => moveBlock(i, i - 1)} className="rounded p-1 text-muted hover:bg-surface-hover hover:text-primary" style={{ minHeight: 0 }} aria-label={`${def(block.kind).label} nach oben`}><Sym name="up" className="h-3.5 w-3.5" /></button>
                    <button onClick={() => moveBlock(i, i + 1)} className="rounded p-1 text-muted hover:bg-surface-hover hover:text-primary" style={{ minHeight: 0 }} aria-label={`${def(block.kind).label} nach unten`}><Sym name="down" className="h-3.5 w-3.5" /></button>
                    <button onClick={() => { const copy = { ...block, id: blockId(), props: { ...block.props } }; change([...blocks.slice(0, i + 1), copy, ...blocks.slice(i + 1)]); }}
                      className="rounded p-1 text-muted hover:bg-surface-hover hover:text-primary" style={{ minHeight: 0 }} aria-label={`${def(block.kind).label} verdoppeln`}><Sym name="copy" className="h-3.5 w-3.5" /></button>
                    <button onClick={() => { change(blocks.filter((b) => b.id !== block.id)); if (selected === block.id) setSelected(null); }}
                      className="rounded p-1 text-muted hover:bg-red-500/10 hover:text-red-600" style={{ minHeight: 0 }} aria-label={`${def(block.kind).label} entfernen`}><Sym name="x" className="h-3.5 w-3.5" /></button>
                  </span>
                </div>
              </li>
            ))}
          </ul>
          <button onClick={() => setAdding(true)} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-default px-2 py-2.5 text-sm font-medium text-secondary hover:border-brand-primary hover:text-brand-primary">
            <Sym name="plus" className="h-4 w-4" />Baustein hinzufügen
          </button>
          {blocks.length === 0 && <p className="text-xs text-muted">Diese Seite ist noch leer.</p>}
        </div>

        {/* Vorschau */}
        <div className="min-h-[600px] min-w-0 overflow-hidden rounded-xl border border-default bg-surface-sunken">
          <div className="flex items-center gap-2 border-b border-default bg-card px-3 py-2">
            <span className="flex gap-1" aria-hidden="true">
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--border-strong)]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--border-strong)]" />
              <span className="h-2.5 w-2.5 rounded-full bg-[var(--border-strong)]" />
            </span>
            <span className="min-w-0 flex-1 truncate text-center text-xs text-muted">Vorschau des Entwurfs – so sieht die Seite nach dem Veröffentlichen aus.</span>
          </div>
          <div className={`p-3 ${mobile ? "mx-auto w-[390px] max-w-full" : "w-full"}`}>
            {previewSrc
              ? <iframe key={nonce} src={previewSrc} title="Vorschau der Seite" className={`h-[700px] w-full border border-default bg-white shadow-sm ${mobile ? "rounded-[1.5rem]" : "rounded-lg"}`} />
              : <EmptyState icon={<Sym name="monitor" className="h-5 w-5" />} title="Die Vorschau ist gerade nicht erreichbar." />}
          </div>
        </div>

        {/* Einstellungen */}
        <div className="min-w-0 space-y-3">
          {current ? (
            <div className="card animate-fade-in">
              <div className="card-header">
                <h2 className="card-title">{def(current.kind).label}</h2>
                <button onClick={() => setSelected(null)} className="btn btn-ghost btn-sm">Schließen</button>
              </div>
              <div className="card-body">
                <p className="mb-4 text-xs leading-relaxed text-muted">{def(current.kind).hint}</p>
                <BlockForm
                  fields={def(current.kind).fields}
                  props={current.props}
                  channels={overview.channels}
                  publicUrl={overview.public_url}
                  onChange={(props) => change(blocks.map((b) => (b.id === current.id ? { ...b, props } : b)))}
                />
              </div>
            </div>
          ) : (
            <p className="flex items-center gap-2 rounded-xl border border-dashed border-default p-4 text-sm text-muted">
              <Sym name="sliders" className="h-4 w-4" />
              Links einen Baustein anklicken, um ihn einzustellen.
            </p>
          )}

          <details className="card group/d" open={!current}>
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-primary">
              Seite<Sym name="down" className="h-4 w-4 text-muted transition-transform group-open/d:rotate-180" />
            </summary>
            <div className="space-y-3 border-t border-default px-4 py-4">
              {metaFeld("Titel", "title")}
              {!page.is_home && metaFeld(pfad.includes("/sender/") ? "Adresse (hinter dem Kanalnamen)" : "Adresse (hinter dem Radionamen)", "slug")}
              {metaFeld("Beschriftung im Menü (leer: Titel)", "menu_label")}
              <label className="flex cursor-pointer items-center gap-2 text-sm text-primary">
                <input type="checkbox" className="h-4 w-4" checked={meta.in_menu} onChange={(e) => { setMeta({ ...meta, in_menu: e.target.checked }); setDirty(true); }} />
                Im Menü zeigen
              </label>
              {metaFeld("Titel für Suchmaschinen (leer: Seitentitel)", "seo_title")}
              <label className="block text-[0.8rem] font-medium text-secondary">Kurzbeschreibung für Suchmaschinen
                <textarea rows={2} value={meta.seo_description} maxLength={300}
                  onChange={(e) => { setMeta({ ...meta, seo_description: e.target.value }); setDirty(true); }}
                  className="input mt-1.5" />
              </label>
            </div>
          </details>

          <details className="card group/d">
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-primary">
              Vorschau-Link zum Weitergeben<Sym name="down" className="h-4 w-4 text-muted transition-transform group-open/d:rotate-180" />
            </summary>
            <div className="border-t border-default px-4 py-4">
              <p className="text-xs text-muted">
                Der Link zeigt den Entwurf und gilt zwei Stunden. Danach sehen Fremde die Seite nur, wenn sie veröffentlicht ist.
              </p>
              <div className="mt-2 flex gap-2">
                <input readOnly value={previewUrl ?? ""} className="input h-8 min-w-0 font-mono text-xs" aria-label="Vorschau-Link" />
                <button onClick={() => { if (previewUrl) void navigator.clipboard?.writeText(previewUrl); }}
                  className="btn btn-secondary btn-sm flex-shrink-0"><Sym name="copy" className="h-3.5 w-3.5" />Kopieren</button>
              </div>
              <button onClick={() => void siteApi.previewLink(pageId).then((r) => { setPreviewUrl(r.url); setNonce((n) => n + 1); })}
                className="btn btn-ghost btn-sm mt-2">Neuen Link erzeugen</button>
            </div>
          </details>

          <details className="card group/d">
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-primary">
              Frühere Fassungen<Sym name="down" className="h-4 w-4 text-muted transition-transform group-open/d:rotate-180" />
            </summary>
            <div className="border-t border-default px-4 py-3">
            {page.versions.length === 0 ? (
              <p className="text-xs text-muted">Beim ersten Veröffentlichen wird eine Fassung gesichert.</p>
            ) : (
              <ul className="space-y-1">
                {page.versions.map((v) => (
                  <li key={v.id} className="flex items-center justify-between gap-2 text-xs">
                    <span className="flex items-center gap-1.5 text-secondary">
                      <Sym name="clock" className="h-3.5 w-3.5 text-muted" />
                      {new Date(v.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                      {v.author ? ` · ${v.author}` : ""}
                    </span>
                    <button
                      onClick={async () => {
                        if (!window.confirm("Diese Fassung in den Entwurf holen? Der jetzige Entwurf wird überschrieben.")) return;
                        const res = await siteApi.restoreVersion(pageId, v.id);
                        setBlocks(res.blocks);
                        setDirty(true);
                        setMessage("Alte Fassung geholt. Noch nicht veröffentlicht.");
                      }}
                      className="btn btn-ghost btn-sm">Zurückholen</button>
                  </li>
                ))}
              </ul>
            )}
            </div>
          </details>
        </div>
      </div>

      {adding && (
        <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4 backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Baustein hinzufügen"
          onClick={(e) => { if (e.target === e.currentTarget) setAdding(false); }}>
          <div className="card mt-8 w-full max-w-3xl animate-fade-in" style={{ boxShadow: "var(--shadow-lg)" }}>
            <div className="card-header">
              <h2 className="text-lg font-semibold text-primary">Baustein hinzufügen</h2>
              <button onClick={() => setAdding(false)} className="btn btn-ghost btn-sm">Schließen</button>
            </div>
            <div className="space-y-5 p-5">
            {GROUP_ORDER.map((group) => (
              <div key={group}>
                <div className="section-label">{group}</div>
                <div className="mt-2 grid gap-2 sm:grid-cols-3">
                  {overview.blocks.filter((b) => b.group === group).map((b) => {
                    const blocked = b.once && usedOnce.has(b.kind);
                    return (
                      <button key={b.kind} onClick={() => addBlock(b.kind)} disabled={blocked}
                        className="rounded-lg border border-default p-3 text-left hover:border-brand-primary hover:bg-brand-primary/[0.04] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-default disabled:hover:bg-transparent">
                        <div className="text-sm font-medium text-primary">{b.label}</div>
                        <div className="mt-0.5 text-xs leading-relaxed text-muted">{blocked ? "Steht schon auf dieser Seite." : b.hint}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Aussehen

function LookTab({ overview, isAdmin, onChanged }: { overview: SiteOverview; isAdmin: boolean; onChanged: () => Promise<void> }): JSX.Element {
  const [settings, setSettings] = useState<SiteSettings>(overview.settings ?? DEFAULT_SITE_SETTINGS);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await siteApi.saveSettings(settings);
      setMessage("Gespeichert. Die Hörerseite übernimmt es sofort.");
      await onChanged();
    } catch {
      setError("Das Speichern hat nicht geklappt.");
    } finally {
      setBusy(false);
    }
  };

  const field = (label: string, key: keyof SiteSettings["legal"], hint?: string, rows = 1): JSX.Element => (
    <label className="block text-[0.8rem] font-medium text-secondary">{label}
      {rows > 1 ? (
        <textarea rows={rows} value={settings.legal[key]} disabled={!isAdmin}
          onChange={(e) => setSettings({ ...settings, legal: { ...settings.legal, [key]: e.target.value } })}
          className="input mt-1.5" />
      ) : (
        <input value={settings.legal[key]} disabled={!isAdmin}
          onChange={(e) => setSettings({ ...settings, legal: { ...settings.legal, [key]: e.target.value } })}
          className="input mt-1.5" />
      )}
      {hint && <span className="field-hint block font-normal">{hint}</span>}
    </label>
  );

  const auswahl = (label: string, value: string, onChange: (v: string) => void, options: Array<[string, string]>): JSX.Element => (
    <label className="block text-[0.8rem] font-medium text-secondary">{label}
      <select value={value} disabled={!isAdmin} onChange={(e) => onChange(e.target.value)} className="input mt-1.5">
        {options.map(([key, text]) => <option key={key} value={key}>{text}</option>)}
      </select>
    </label>
  );

  return (
    <div className="max-w-4xl space-y-5">
      <Card title="Schrift, Ecken, Dichte" description="Die Farben kommen weiter aus „Hörerseite & Farben“. Hier stellt ihr ein, wie die Seite sich anfühlt.">
        <p className="-mt-1 mb-4 flex items-start gap-2 text-xs text-muted">
          <Sym name="shield" className="mt-px h-3.5 w-3.5" />
          Alle Schriften sind auf den Geräten der Hörer schon vorhanden – es wird nichts von fremden Servern nachgeladen.
        </p>
        <div className="grid gap-4 sm:grid-cols-3">
          {auswahl("Schriftpaar", settings.font_set, (v) => setSettings({ ...settings, font_set: v as SiteSettings["font_set"] }),
            Object.entries(FONT_SETS).map(([key, set]) => [key, set.label]))}
          {auswahl("Ecken", settings.radius, (v) => setSettings({ ...settings, radius: v as SiteSettings["radius"] }),
            Object.keys(RADIUS).map((key) => [key, key]))}
          {auswahl("Dichte", settings.density, (v) => setSettings({ ...settings, density: v as SiteSettings["density"] }),
            Object.keys(DENSITY).map((key) => [key, key]))}
        </div>
      </Card>

      <Card title="Impressum & Datenschutz" description="Diese Angaben setzt der Baustein „Rechtliches“ in eure Seite ein. Den Datenschutztext schreibt die Suite dazu – er beschreibt, was auf eurer Seite wirklich passiert.">
        <div className="grid gap-4 sm:grid-cols-2">
          {field("Wer betreibt das Radio?", "operator", "Verein, Firma oder Name")}
          {field("Anschrift", "address", "Straße, PLZ, Ort", 3)}
          {field("Kontakt", "contact", "E-Mail und/oder Telefon")}
          {field("Verantwortlich für den Inhalt", "responsible", "Name und Anschrift")}
          {field("Ansprechpartner für Datenschutz", "privacy_contact")}
          {field("Weitere Angaben", "extra", "Registergericht, Umsatzsteuer-Nummer, Aufsichtsbehörde …", 4)}
        </div>
      </Card>

      <Card title="Besucher zählen (freiwillig)" description="Ohne Eintrag zählt niemand mit – die Seite kommt ohne Cookies aus. Wer mag, trägt die Adresse einer eigenen Matomo-Installation ein. Fremde Skripte lassen sich hier bewusst nicht eintragen.">
        <div className="grid gap-4 sm:grid-cols-[minmax(0,1fr)_10rem]">
          <label className="block text-[0.8rem] font-medium text-secondary">Adresse von Matomo
            <input value={settings.matomo_url} disabled={!isAdmin} placeholder="https://zaehler.example.org"
              onChange={(e) => setSettings({ ...settings, matomo_url: e.target.value })}
              className="input mt-1.5" />
          </label>
          <label className="block text-[0.8rem] font-medium text-secondary">Nummer der Website
            <input value={settings.matomo_site_id} disabled={!isAdmin} placeholder="1"
              onChange={(e) => setSettings({ ...settings, matomo_site_id: e.target.value })}
              className="input mt-1.5" />
          </label>
        </div>
      </Card>

      <Meldungen error={error} notice={message} onClear={() => setMessage(null)} />
      {isAdmin
        ? <button onClick={() => void save()} disabled={busy} className="btn btn-primary">Speichern</button>
        : <p className="text-sm text-muted">Ändern darf das die Leitung.</p>}
    </div>
  );
}

// ---------------------------------------------------------------- Bilder

function ImagesTab({ publicUrl }: { publicUrl: string }): JSX.Element {
  const { state, error, busy, upload, rename, remove } = useMediaLibrary();
  const [file, setFile] = useState<File | null>(null);
  const [alt, setAlt] = useState("");
  const fileRef = useRef<HTMLInputElement | null>(null);
  const full = state.quota > 0 ? Math.round((state.used / state.quota) * 100) : 0;

  return (
    <div className="space-y-5">
      <Card title="Bilder hochladen" description="Große Bilder werden automatisch verkleinert und in ein sparsames Format umgewandelt. Die Beschreibung ist Pflicht: Menschen, die nicht sehen können, bekommen sie vorgelesen.">
        <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto] sm:items-end">
          <label className="block text-[0.8rem] font-medium text-secondary">Datei
            <input ref={fileRef} type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] ?? null)} aria-label="Bilddatei"
              className="mt-1.5 block w-full rounded-lg border border-dashed border-default bg-input px-3 py-1.5 text-sm text-secondary file:mr-3 file:rounded-md file:border-0 file:bg-brand-primary/10 file:px-2.5 file:py-1 file:text-xs file:font-medium file:text-brand-primary" />
          </label>
          <label className="block text-[0.8rem] font-medium text-secondary">Beschreibung
            <input value={alt} onChange={(e) => setAlt(e.target.value)} maxLength={200} placeholder="Was ist auf dem Bild zu sehen?"
              aria-label="Bildbeschreibung" className="input mt-1.5" />
          </label>
          <button
            onClick={async () => {
              if (!file || alt.trim() === "") return;
              const item = await upload(file, alt.trim());
              if (item) { setFile(null); setAlt(""); if (fileRef.current) fileRef.current.value = ""; }
            }}
            disabled={!file || alt.trim() === "" || busy}
            className="btn btn-primary">
            <Sym name="image" />{busy ? "Lädt …" : "Hochladen"}
          </button>
        </div>
        <div className="mt-5 max-w-sm text-xs text-muted">
          <div className="flex justify-between">
            <span>Belegt: {formatBytes(state.used)} von {formatBytes(state.quota)}</span>
            <span className="tabular-nums">({full} %)</span>
          </div>
          <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-input" style={{ boxShadow: "inset 0 0 0 1px var(--border)" }}>
            <div className={`h-full rounded-full ${full >= 90 ? "bg-red-500" : full >= 75 ? "bg-amber-500" : "bg-brand-primary"}`} style={{ width: `${Math.min(100, full)}%` }} />
          </div>
        </div>
        {error && <Callout tone="danger" className="mt-3">{error}</Callout>}
      </Card>

      {state.items.length === 0 ? (
        <div className="card p-2">
          <EmptyState icon={<Sym name="image" className="h-5 w-5" />} title="Noch keine Bilder da." />
        </div>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {state.items.map((item) => (
            <li key={item.id} className="card group overflow-hidden">
              <div className="aspect-[4/3] bg-input">
                <img src={`${publicUrl}${item.url}`} alt={item.alt} className="h-full w-full object-cover" loading="lazy" />
              </div>
              <div className="p-3">
                <input defaultValue={item.alt} onBlur={(e) => { if (e.target.value.trim() && e.target.value !== item.alt) void rename(item.id, e.target.value.trim()); }}
                  aria-label={`Beschreibung von ${item.original_name ?? "Bild"}`}
                  className="input h-8 text-xs" />
                <div className="mt-2 flex items-center justify-between text-xs text-muted">
                  <span>{item.width}×{item.height} · {formatBytes(item.bytes)}</span>
                  <button onClick={() => { if (window.confirm("Bild löschen? Wo es benutzt wird, bleibt dann eine Lücke.")) void remove(item.id); }}
                    className="btn btn-ghost btn-sm h-7 px-2 !text-red-600 dark:!text-red-400"><Sym name="trash" className="h-3.5 w-3.5" />Löschen</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- Kontakt

function ContactTab({ onChanged }: { onChanged: () => Promise<void> }): JSX.Element {
  const [items, setItems] = useState<ContactMessage[] | null>(null);

  const load = useCallback(async () => {
    setItems(await siteApi.contactMessages().catch(() => []));
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (!items) return <div className="space-y-2" aria-label="Wird geladen …"><Skeleton className="h-24" /><Skeleton className="h-24" /></div>;
  if (items.length === 0) {
    return (
      <div className="card p-2">
        <EmptyState icon={<Sym name="mail" className="h-5 w-5" />} title="Noch keine Nachrichten über das Kontaktformular." />
      </div>
    );
  }

  return (
    <ul className="max-w-4xl space-y-3">
      {items.map((m) => (
        <li key={m.id} className={`card p-4 sm:p-5 ${m.handled_at ? "opacity-60" : ""}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="flex min-w-0 items-start gap-3">
              <Initialen name={m.name || m.email} />
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-primary">{m.subject || "Ohne Betreff"}</span>
                  {m.handled_at ? <Badge tone="success">erledigt</Badge> : <Badge tone="info" dot>offen</Badge>}
                </div>
                <div className="mt-0.5 text-xs text-muted">
                  {m.name} · <a href={`mailto:${m.email}`} className="text-brand-primary underline-offset-2 hover:underline">{m.email}</a> ·{" "}
                  {new Date(m.created_at).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                </div>
              </div>
            </div>
            <button
              onClick={async () => { await siteApi.markContact(m.id, !m.handled_at); await load(); await onChanged(); }}
              className={`btn btn-sm ${m.handled_at ? "btn-ghost" : "btn-secondary"}`}>
              {m.handled_at ? "Wieder offen" : <><Sym name="check" className="h-3.5 w-3.5" />Erledigt</>}
            </button>
          </div>
          <p className="mt-3 whitespace-pre-wrap pl-12 text-sm leading-relaxed text-secondary">{m.body}</p>
        </li>
      ))}
    </ul>
  );
}
